import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ChevronLeft, ChevronRight } from 'lucide-react';
import UserMenu from './UserMenu';
import OrganizationSwitcher from './OrganizationSwitcher';
import { useIsAdmin } from '@/hooks/useUserRole';
import { useUnreadNotificationsCount } from '@/hooks/useNotifications';
import { useOrganization } from '@/contexts/OrganizationContext';
import { trackEvent } from '@/lib/mixpanel';
import { getVisibleMenuSections } from './sidebar/sidebarMenuConfig';

interface SidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  onCollapsedChange: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeTab, setActiveTab, onCollapsedChange }) => {
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const { data: unreadCount = 0 } = useUnreadNotificationsCount();
  const { currentOrganization } = useOrganization();
  const isBrandOrg = currentOrganization?.organization_type === 'brand';
  const [isCollapsed, setIsCollapsed] = useState(false);

  const menuSections = useMemo(
    () => getVisibleMenuSections({ isBrandOrg, isAdmin }),
    [isBrandOrg, isAdmin]
  );

  const handleMenuClick = (id: string, path: string) => {
    setActiveTab(id);
    trackEvent('Navigation Click', { destination: id });
    navigate(path);
  };

  const handleToggleCollapse = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    onCollapsedChange(next);
  };

  return (
    <div
      className={`bg-white border-r border-gray-200 h-screen fixed left-0 top-0 z-30 flex flex-col transition-all duration-300 ease-in-out ${isCollapsed ? 'w-[72px]' : 'w-64'
        }`}
    >
      {/* Collapse toggle button - floats on the right edge */}
      <button
        id="sidebar-toggle-btn"
        onClick={handleToggleCollapse}
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className="absolute -right-3.5 top-8 z-40 w-7 h-7 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm hover:bg-[#A655F8] hover:text-white hover:border-[#A655F8] transition-colors duration-200 text-gray-500"
      >
        {isCollapsed ? (
          <ChevronRight className="w-4 h-4" />
        ) : (
          <ChevronLeft className="w-4 h-4" />
        )}
      </button>

      {/* Logo - Fixed at top */}
      <div className={`border-b border-gray-200 flex-shrink-0 flex items-center ${isCollapsed ? 'p-4 justify-center' : 'p-6'}`}>
        {isCollapsed ? (
          <img src="/favicon.ico" alt="Opsero" className="w-8 h-8 object-contain" />
        ) : (
          <div>
            <div className="flex items-center gap-2">
              <img src="/favicon.ico" alt="Opsero" className="w-10 h-10 object-contain" />
              <h1 className="text-2xl font-bold bg-[#A655F8] bg-clip-text text-transparent">
                Opsero
              </h1>
            </div>
            <p className="text-sm text-gray-500 mt-1 pl-10">
              {isBrandOrg ? 'Brand Marketing' : 'Agency CRM'}
            </p>
          </div>
        )}
      </div>

      {/* Organization Switcher - Fixed */}
      <div className={`border-b border-gray-200 flex-shrink-0 ${isCollapsed ? 'p-3 flex justify-center' : 'p-4'}`}>
        <OrganizationSwitcher isCollapsed={isCollapsed} />
      </div>

      {/* User Profile - Fixed */}
      <div className={`border-b border-gray-200 flex-shrink-0 ${isCollapsed ? 'p-3 flex justify-center' : 'p-4'}`}>
        <UserMenu isCollapsed={isCollapsed} />
      </div>

      {/* Navigation - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <nav className={`${isCollapsed ? 'p-2' : 'px-3 py-2'}`}>
          {menuSections.map((section, sectionIdx) => (
            <div key={section.title} className={sectionIdx > 0 ? (isCollapsed ? 'mt-1.5' : 'mt-3') : ''}>
              {!isCollapsed ? (
                <h3 className="px-3 pt-1.5 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                  {section.title}
                </h3>
              ) : (
                sectionIdx > 0 && <div className="my-1.5 mx-1.5 border-t border-gray-300" />
              )}
              <ul className="space-y-0.5">
                {section.items.map((item) => {
                  const isActive = activeTab === item.id;
                  const Icon = item.icon;
                  return (
                    <li key={item.id}>
                      <button
                        id={`nav-${item.id}`}
                        title={isCollapsed ? item.label : undefined}
                        onClick={() => handleMenuClick(item.id, item.path)}
                        className={`w-full flex items-center rounded-lg transition-colors ${isCollapsed ? 'justify-center px-2 py-2' : 'px-3 py-1.5'
                          } text-sm font-medium ${isActive
                            ? 'bg-[#F8F3FF] text-[#7F22FE]'
                            : 'text-gray-900'
                          }`}
                      >
                        <Icon
                          className={`w-4 h-4 flex-shrink-0 ${isCollapsed ? '' : 'mr-3'
                            } ${isActive ? 'text-[#7F22FE]' : 'text-gray-900'}`}
                        />
                        {!isCollapsed && <span>{item.label}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </div>

      {/* Notification Bell - Fixed at bottom */}
      <div className={`flex-shrink-0 ${isCollapsed ? 'p-2 flex justify-center' : 'p-3'}`}>
        {unreadCount > 0 ? (
          isCollapsed ? (
            <div
              className="relative flex items-center justify-center w-8 h-8 bg-purple-50 rounded-lg"
              title={`${unreadCount} new notification${unreadCount !== 1 ? 's' : ''}`}
            >
              <Bell className="w-4 h-4 text-[#A655F8]" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#A655F8] rounded-full text-[10px] text-white flex items-center justify-center font-bold">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-between px-3 py-2 bg-purple-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <Bell className="w-4 h-4 text-[#A655F8]" />
                <span className="text-xs font-medium text-[#A655F8]">
                  {unreadCount} new notification{unreadCount !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          )
        ) : null}
      </div>
    </div>
  );
};

export default Sidebar;
